import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { AdminPagesHeader } from '@atlassian/jira-admin-pages-common/src/ui/admin-pages-header/index.tsx';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import { PermissionGenericErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/permission-generic-error-boundary/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4';
import { usePlansItems } from '@atlassian/jira-plans-item-store';
import {
	FireScreenAnalytics,
	SCREEN,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { issueLinkInformationResource } from '@atlassian/jira-router-resources-advanced-roadmaps-settings/src/services/issue-link';
import { JIRA_PORTFOLIO } from '@atlassian/jira-shared-types/src/application.tsx';
import { PermissionGenericError } from '@atlassian/jira-standard-errors/src/common/utils/permission-generic-error/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { useResource } from '@atlassian/react-resource-router';
import { Provider } from '../controllers';
import { mapToOriginalState } from '../controllers/utils';
import { DependencyList } from './dependency-list';
import { Footer } from './footer';
import { messages } from './messages';
import { Subheader } from './subheader';
import { SuccessFlags } from './success-flags';

export const DependencySettingsPage = () => (
	<PermissionGenericErrorBoundary>
		<DependencySettingsPageComponent />
	</PermissionGenericErrorBoundary>
);

const DependencySettingsPageComponent = () => {
	const { formatMessage } = useIntl();
	const { software: edition } = useAppEditions();
	const [{ items }, { load }] = usePlansItems();
	useEffect(() => {
		getWillShowNav4() && load();
	}, [load]);

	const isPlansAdmin = fg('plans-settings-pages-permission-check')
		? Boolean(items?.links?.settings?.isVisible)
		: true;

	const { data, loading } = useResource(issueLinkInformationResource);

	if (loading || data === null || (items === null && fg('plans-settings-pages-permission-check'))) {
		return (
			<>
				<AdminPagesHeader title={formatMessage(messages.pageHeading)} />
				<Center>
					<Spinner size="large" />
				</Center>
			</>
		);
	}

	if (!isPlansAdmin && fg('plans-settings-pages-permission-check')) {
		const permissionError = new PermissionGenericError('User is not Plans Admin');
		fireErrorAnalytics({
			meta: {
				id: 'PlansDependencySettingsPermissionError',
				packageName: 'jiraPortfolio3DependencySettings',
			},
			error: permissionError,
		});
		throw permissionError;
	}

	const originalState = mapToOriginalState(data);
	return (
		<UFOSegment name="advanced-roadmaps-global-dependency-settings">
			<ViewExperienceTrackingProvider
				experience="viewPortfolio3GlobalDependencySettings"
				analyticsSource="portfolio3"
				application={JIRA_PORTFOLIO}
				edition={edition}
			>
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="dependencySettings">
					<FireScreenAnalytics attributes={{}} />
					<Provider initialState={originalState}>
						<AdminPagesHeader title={formatMessage(messages.pageHeading)} />
						<Subheader />
						<Box xcss={dependencyListContainerStyles}>
							<DependencyList />
						</Box>
						<Footer />
						<SuccessFlags />
					</Provider>
				</ContextualAnalyticsData>
			</ViewExperienceTrackingProvider>
		</UFOSegment>
	);
};
const dependencyListContainerStyles = xcss({
	marginTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Center = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});
